<script setup lang="ts">
import type { Project } from '~/utils/project/Project'
import type { AddonOpportunity, Addons } from '~/utils/simulation/Simulation'
import { SolButton } from '@solfacil/girassol'
import EditIcon from '~icons/material-symbols/edit'
import { useMoney } from '~/composables/money'
import { FEATURE_FLAGS } from '~/constants'
import useAuth from '~/store-v2/auth/useAuth'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import useFinancing from '~/store-v2/financings/useFinancing'
import useProjectContractSigned from '~/store-v2/projects/useProjectContractSigned'
import useProjectProposalChanges from '~/store-v2/projects/useProjectProposalChanges'
import currency from '~/utils/currency'
import { calculateFirstDueDate } from '~/utils/simulation/calculations'
import PriceUpdateModal from '../details/PriceUpdateModal.vue'

const props = defineProps<{
  project: Project
  selectedGracePeriod: number
  selectedCommission: number
  selectedDueDay?: number | null
  defaultFirstDueDate?: string | null
  disabledFields: boolean
  projectIsFinished: boolean
  loadingConclusion: boolean
  isDetails: boolean
}>()

const { data: auth } = useAuth()

const { t } = useI18n()
const router = useRouter()
const route = useRoute<any>()
const id = computed(() => route.params.id as string)
const routeName = computed(() => route.name)

const {
  data: proposalChanges,
  isLoading: isLoadingProposalChanges,
  isError: isErrorProposalChanges,
} = useProjectProposalChanges(id)

const {
  data: isProjectContractSigned,
  isLoading: isLoadingProjectContractSigned,
} = useProjectContractSigned(id)

const {
  data: financing,
} = useFinancing(id)

const isBiometrySubmitted = computed(() => {
  return !!financing.value?.section_statuses?.biometry_and_contract?.metadata?.biometry?.last_submitted_at
})

const canEditSimulation = computed(() => {
  return !isProjectContractSigned.value && !isBiometrySubmitted.value
})

const { formatMoney } = useMoney()

const {
  getSelectedInstallment,
  getValueOfAmpera,
  getValueOfAmperaMontlhyInterestRate,
  hasInsuranceApplied,
  getInsurancePartnerGainSum,
  getSimulationOpportunities,
  hasCommissionApplied,
  getSumOfInsurancesAndCommissions,
  hasAmperaOrInsurance,
  getCetValue,
  getSumOfInsurancesAppliedFromStore,
} = useResumeStore()

const { getAddonsList } = useAddonStore()

const blockFields = ref(false)
const loadingLocal = ref(false)
const priceUpdateModal = ref(false)

const disabledButton = ref(true)

const userBlack = auth.value?.person.partner?.sfplus?.is_level_black
const difference = ref<number>(0)

const { getAddonsApplieds } = useAddonStore()

const responseSimulationOpportunities: any = ref({})

const { data: comboConnectEnabledData } = useFeatureFlag<boolean>(FEATURE_FLAGS.COMBO_CONNECT_ENABLED_FOR_INTEGRATOR, false, true)
const comboConnectEnabledValue = computed(() => comboConnectEnabledData.value || props.project.selected_installments?.is_combo_connect || false)
const { data: enableDueDateChangeData } = useFeatureFlag<boolean>(FEATURE_FLAGS.ENABLE_DUE_DATE_CHANGE, false, true)
const enableDueDateChangeValue = computed(() => enableDueDateChangeData.value || false)

onMounted(async () => {
  await simulationOpportunities()
  await addSolfacilPoints()
  await itemsNoApplied()
  insertInformationTooltip(getAddonsApplieds())
})

onUpdated(() => {
  const selectedInstallment = getSelectedInstallment()
  if (selectedInstallment)
    difference.value = (selectedInstallment.value - selectedInstallment.down_payment) || 0

  disabledButton.value = !props.disabledFields || blockFields.value || !getSelectedInstallment() || loadingLocal.value
})

watch(() => getSimulationOpportunities(), async () => {
  await simulationOpportunities()
  addSolfacilPoints()
  itemsNoApplied()
})

watch(() => getAddonsApplieds(), async (addonsApplieds) => {
  await simulationOpportunities()
  addSolfacilPoints()
  itemsNoApplied()
  insertInformationTooltip(addonsApplieds)
})

function hasPrestamista() {
  return getAddonsList().some((addon: Addons) => addon.product_slug.toLowerCase() === 'prestamista')
}

const totalPoints = ref(0)
const strengthAddons = ref(0)

async function addSolfacilPoints() {
  totalPoints.value = 0
  strengthAddons.value = 0

  const { addons_opportunities, financing_gained_points } = responseSimulationOpportunities.value || {}

  const addonsApplied = props.project.complete ? props.project.selected_installments.addons : getAddonsApplieds()

  addonsApplied.forEach((getAddonApplied) => {
    const matchingAddon = addons_opportunities?.find((addon: { addon_type: string, product_slug: string }) => {
      if (addon.addon_type === 'insurance')
        return addon.product_slug === getAddonApplied.product_slug
      return addon.addon_type === getAddonApplied.type
    })
    if (matchingAddon) {
      totalPoints.value += matchingAddon.gained_points

      if (props.project.person_type.toLowerCase() === 'pj')
        strengthAddons.value += 2

      if (props.project.person_type.toLowerCase() === 'pf') {
        !hasPrestamista()
          ? strengthAddons.value += (matchingAddon.addon_type === 'combo')
            ? 2
            : 1
          : strengthAddons.value += 1
      }
    }
  })

  totalPoints.value += financing_gained_points || 0
}

const canAddItems = ref([])

function itemsNoApplied() {
  const addonsApplieds: Addons[] = getAddonsApplieds()

  if (responseSimulationOpportunities?.value?.addons_opportunities) {
    const allOpportunities = responseSimulationOpportunities.value.addons_opportunities.map((addon: AddonOpportunity) => {
      if (addon.addon_type === 'insurance') {
        return addon.product_slug
          ? addon.product_slug.toLowerCase()
          : ''
      }
      return addon.addon_type.toLowerCase()
    })
    const itemsApplieds = addonsApplieds.map((addon: Addons) => (addon.addon_type || addon.product_slug).toLowerCase())

    canAddItems.value = allOpportunities.filter((value: string) => !itemsApplieds.includes(value))
  }
}

function formattedTextTooltip(strengthAddons: number) {
  const itemsList = formatItemsList(canAddItems.value)
  const words = replaceWords(itemsList)
  const personType = props.project.person_type.toLowerCase()

  const phrases = {
    pf: {
      1: `${t('solfacilPoints.phrases.one')} ${words}?`,
      2: `${t('solfacilPoints.phrases.two')} ${words}?`,
      3: `${t('solfacilPoints.phrases.three', { string: words })}`,
      4: `${t('solfacilPoints.phrases.four')}`,
    },
    pj: {
      2: `${t('solfacilPoints.phrases.two')} ${words}?`,
      4: `${t('solfacilPoints.phrases.four')}`,
    },
  }

  if (personType === 'pf' && phrases.pf[strengthAddons as 1 | 2 | 3 | 4])
    return phrases.pf[strengthAddons as 1 | 2 | 3 | 4]

  if (personType === 'pj' && phrases.pj[strengthAddons as 2 | 4])
    return phrases.pj[strengthAddons as 2 | 4]

  return ''
}

const commissionValues = ref('')

function insertInformationTooltip(addonsApplieds: Addons[]) {
  const selectedInstallment = getSelectedInstallment()
  const defaultCommission = props.project.selected_installments?.commission_raw || 0
  const comissionValue = selectedInstallment?.commission_raw || defaultCommission

  commissionValues.value = `Financiamento: ${formatMoney(comissionValue)}\n`

  const insuranceCommissionValues = addonsApplieds.filter((item: Addons) => item.type === 'insurance')

  if (insuranceCommissionValues.length) {
    const formattedText = insuranceCommissionValues.map((el: Addons) => {
      const productSlugFormatted = el.product_slug
        .toLowerCase()
        .replace(/riscos diversos/g, 'Seguro do Equipamento Solar')
        .replace(/prestamista/g, 'Seguro Proteção Financeira')

      return `${productSlugFormatted}: ${formatMoney(el.partner_commission)}\n`
    })

    commissionValues.value = `${commissionValues.value} ${formattedText.join(' ')}`
  }

  commissionValues.value = commissionValues.value.replace(/\n\s+(S)/g, '\n$1')
}

function formatItemsList(items: string[]) {
  if (props.project.person_type.toLowerCase() === 'pj')
    items = items.filter(item => item !== 'prestamista' && item !== 'riscos diversos')

  if (props.project.person_type.toLowerCase() === 'pf' && !hasPrestamista())
    items = items.filter(item => item !== 'prestamista')

  const formattedString = items
    .slice(0, -1)
    .join(', ')
    + (items.length > 1 ? ' e ' : '')
    + items.slice(-1)
  return formattedString
}

function replaceWords(inputString: string) {
  const replacedString = inputString
    .replace(/riscos diversos/g, 'seguro do equipamento solar')
    .replace(/prestamista/g, 'seguro proteção financeira')

  return replacedString
}

async function simulationOpportunities() {
  responseSimulationOpportunities.value = await getSimulationOpportunities()
}

function getGracePeriodMonth(value: number) {
  if (value === 1)
    return 'Mês'

  return 'Meses'
}

const firstDueDate = computed(() => calculateFirstDueDate(
  props.defaultFirstDueDate,
  !props.disabledFields,
  props.selectedGracePeriod,
  props.selectedDueDay,
))

const shouldShowDueDay = computed(() => {
  if (!enableDueDateChangeValue.value) return false

  const hasEditQuery = 'edit' in route.query

  if (hasEditQuery) {
    return !!props.selectedDueDay
  }

  return !props.disabledFields || !!props.selectedDueDay
})

const shouldShowFirstDueDate = computed(() => {
  if (!enableDueDateChangeValue.value) return false

  const hasEditQuery = 'edit' in route.query

  if (hasEditQuery) {
    return !!firstDueDate.value
  }

  return !props.disabledFields || !!firstDueDate.value
})

function convertAnnualToMonthlyRate(monthlyRate?: number) {
  if (monthlyRate)
    return monthlyRate?.toFixed(2)?.replace('.', ',')

  return '--'
}

function getInterestRateValue(value?: number) {
  if (value)
    return value?.toFixed(2)?.replace('.', ',')

  return '--'
}

const comboFacilValue = computed(() => {
  if (props.projectIsFinished) {
    const comboAddon = props.project.selected_installments.addons.find(addon => /combo/i.test(addon.type))
    const comboPrice = comboAddon?.installment_price ?? 0
    return comboPrice
  }
  const value = (getSelectedInstallment()?.difference ?? 0) * -1
  return value
})

const comboConecteValue = computed(() => {
  if (props.projectIsFinished) {
    const comboConecteAddon = props.project.selected_installments.addons.find(addon =>
      addon.type === 'combo_connect' || addon.product_slug === 'Combo Connect',
    )
    const comboConectePrice = comboConecteAddon?.installment_price ?? 0
    const amperaValue = props.project.selected_installments.addons.find(addon => /ampera/i.test(addon.type))?.installment_price ?? 0
    const insuranceValue = props.project.selected_installments.addons.reduce((acc, addon) =>
      addon.type === 'insurance' && addon.applied ? acc + addon.installment_price : acc, 0)

    return ((comboConectePrice * -1) + props.project.selected_installments.installment_value) - (amperaValue + insuranceValue)
  }
  const value = getSelectedInstallment()?.type === 'Combo Conecte'
    ? (getSelectedInstallment()?.difference ?? 0) * -1
    : 0
  return value
})

const comboConecteDiscount = computed(() => {
  if (props.projectIsFinished) {
    const comboConecteAddon = props.project.selected_installments.addons.find(addon =>
      addon.type === 'combo_connect' || addon.product_slug === 'Combo Connect',
    )
    return comboConecteAddon?.installment_price ?? 0
  }

  const result = getSelectedInstallment()?.combo_conecte
    ? (getSelectedInstallment()?.difference ?? 0) * -1
    : 0

  return result
})

const comboDiscout = computed(() => {
  const comboAddon = props.project.selected_installments.addons.find(addon => /combo/i.test(addon.type))
  const comboPrice = comboAddon?.installment_price ?? 0
  const amperaValue = props.project.selected_installments.addons.find(addon => /ampera/i.test(addon.type))?.installment_price ?? 0
  const insuranceValue = props.project.selected_installments.addons.reduce((acc, addon) =>
    addon.type === 'insurance' && addon.applied ? acc + addon.installment_price : acc, 0)

  return ((comboPrice * -1) + props.project.selected_installments.installment_value) - (amperaValue + insuranceValue)
})

const shouldShowComboConecteDetails = computed(() => {
  return !getSelectedInstallment()?.combo_conecte_only
    && (comboConnectEnabledValue.value || getSelectedInstallment()?.combo_conecte)
    && (getSelectedInstallment()?.combo_conecte || getSelectedInstallment()?.type === 'Combo Conecte')
})
</script>

<template>
  <div id="resume" class="resume-container !mt-0">
    <h2 class="fonts-body-x-large-bold mb-micro" data-testid="resume-title">
      Resumo
    </h2>
    <h3 class="card-subtitle my-micro" data-testid="installment-subtitle">
      Parcela
    </h3>
    <div class="resume-card" data-testid="resume-card">
      <span
        v-if="(getSelectedInstallment()?.combo && !(comboConnectEnabledValue && getSelectedInstallment()?.type === 'Combo Conecte')) && props.projectIsFinished"
        class="discount"
        data-testid="combo-discount-finished"
      >
        {{ currency.formatBRL(comboDiscout) }}
      </span>

      <span
        v-if="comboConnectEnabledValue && getSelectedInstallment()?.type === 'Combo Conecte' && props.projectIsFinished"
        class="discount"
        data-testid="combo-conecte-discount-finished"
      >
        {{ currency.formatBRL(comboConecteValue) }}
      </span>

      <span v-if="props.projectIsFinished && getSelectedInstallment()?.installments_value" class="installment-amount" data-testid="installment-amount-finished">
        <span v-if="getSelectedInstallment() && props.projectIsFinished" class="installment-size" data-testid="installment-size-finished">
          {{ getSelectedInstallment()?.installments_quantity }}x
        </span>
        {{ formatMoney(
          props.project.complete
            ? getSelectedInstallment()?.installments_value
            : ((getSelectedInstallment()?.installments_value ?? 0) + getSumOfInsurancesAppliedFromStore()) + getValueOfAmpera(),
        ) ?? "R$ --" }}
      </span>

      <span v-if="getSelectedInstallment()?.combo && !getSelectedInstallment()?.combo_conecte_only && !props.projectIsFinished" class="discount" data-testid="combo-discount-unfinished">
        {{ formatMoney((getSelectedInstallment()?.installments_value ?? 0) + (getSelectedInstallment()?.difference ?? 0)) }}
      </span>

      <span v-if="getSelectedInstallment()?.combo && !props.projectIsFinished" class="installment-amount" data-testid="installment-amount-unfinished">
        <span v-if="getSelectedInstallment()" class="installment-size" data-testid="installment-size-unfinished">
          {{ getSelectedInstallment() && getSelectedInstallment()?.installments_quantity }}x
        </span>
        {{ formatMoney(((getSelectedInstallment()?.installments_value ?? 0) + getSumOfInsurancesAppliedFromStore()) + getValueOfAmpera()) ?? "R$ --" }}
      </span>

      <span v-else-if="!props.projectIsFinished && getSelectedInstallment()?.installments_value" class="resume-value" data-testid="resume-value-unfinished">
        <span v-if="getSelectedInstallment()" class="fonts-subtitle-small">
          {{ getSelectedInstallment()?.installments_quantity }}x
        </span>
        {{ formatMoney(((getSelectedInstallment()?.installments_value ?? 0) + getSumOfInsurancesAppliedFromStore()) + getValueOfAmpera()) ?? "R$ --" }}
      </span>
    </div>
    <div class="container-details">
      <ul class="flex flex-col">
        <li v-if="getSelectedInstallment()?.combo && (!getSelectedInstallment()?.combo_conecte && !(getSelectedInstallment()?.type === 'Combo Conecte'))" class="details" data-testid="combo-details">
          <span class="text-brand-primary-medium">{{ $t('installments.combo_facil_label') }}</span>
          <span class="text-brand-secondary-pure">{{ currency.formatBRL(comboFacilValue) }}</span>
        </li>
        <li v-if="shouldShowComboConecteDetails" class="details" data-testid="combo-conecte-details">
          <span class="text-brand-primary-medium">{{ $t('installments.combo_conecte_label') }}</span>
          <span class="text-brand-secondary-pure">{{ currency.formatBRL(comboConecteDiscount) }}</span>
        </li>
        <li v-if="getValueOfAmpera()" class="details" data-testid="ampera-details">
          <span class="text-brand-primary-medium">{{ $t('installments.ampera_label') }}</span>
          <span class="text-brand-secondary-pure">{{ formatMoney(getValueOfAmpera()) }}</span>
        </li>
        <li v-if="hasInsuranceApplied()" class="details" data-testid="insurance-details">
          <span class="text-brand-primary-medium">{{ $t('installments.insurance_label') }}</span>
          <span class="text-brand-primary-medium">+{{ formatMoney(getSumOfInsurancesAppliedFromStore()) }}</span>
        </li>
      </ul>
    </div>

    <div class="resume-info">
      <h3 class="card-subtitle mt-4xs">
        Financiamento
      </h3>
      <div class="container-details">
        <ul class="flex flex-col">
          <li class="details" data-testid="interest-rate">
            <span class="text-brand-primary-medium">Taxa de juros<span v-if="userBlack"> Black</span></span>
            <span class="text-brand-primary-pure">
              {{ convertAnnualToMonthlyRate(getValueOfAmperaMontlhyInterestRate()
                ? getValueOfAmperaMontlhyInterestRate()
                : getSelectedInstallment()?.monthly_interest_rate) }} % a.m
            </span>
          </li>
          <li class="details" data-testid="cet">
            <span class="text-brand-primary-medium">CET<span v-if="userBlack"> Black</span></span>
            <span class="text-brand-primary-pure">{{ getInterestRateValue(hasAmperaOrInsurance()
              ? getCetValue()
              : getSelectedInstallment()?.cet) }} % a.m</span>
          </li>
          <li class="details flex flex-col" data-testid="project-values">
            <div class="flex justify-between py-quark " data-testid="total-project-value">
              <span class="text-brand-primary-medium">Valor total do projeto</span>
              <span class="text-brand-primary-pure">{{ formatMoney(getSelectedInstallment()?.value) }}</span>
            </div>
            <div class="flex justify-between py-quark pl-micro" data-testid="down-payment">
              <span class="text-brand-primary-medium">Entrada</span>
              <span class="text-brand-primary-pure">{{ formatMoney(getSelectedInstallment()?.down_payment) }}</span>
            </div>
            <div class="flex justify-between py-quark pl-micro" data-testid="financed-value">
              <span class="text-brand-primary-medium">Valor Financiado</span>
              <span class="text-brand-primary-pure">{{ formatMoney(difference) }}</span>
            </div>
          </li>
          <li class="details flex flex-col" data-testid="project-period">
            <div class="flex justify-between py-quark " data-testid="grace-period">
              <span class="text-brand-primary-medium">Carência</span>
              <span class="text-brand-primary-pure">{{ props.selectedGracePeriod }} {{ getGracePeriodMonth(props.selectedGracePeriod) }}</span>
            </div>
            <div v-if="shouldShowDueDay" class="flex justify-between py-quark" data-testid="due-day">
              <span class="text-brand-primary-medium">Dia do vencimento</span>
              <span class="text-brand-primary-pure">{{ props.selectedDueDay }}</span>
            </div>
            <div v-if="shouldShowFirstDueDate" class="flex justify-between py-quark pl-micro" data-testid="first-due-date">
              <span class="text-brand-primary-medium">Data do 1º venc</span>
              <span class="text-brand-primary-pure">{{ firstDueDate }}</span>
            </div>
          </li>
        </ul>
      </div>

      <div>
        <h3
          v-if="hasInsuranceApplied() || hasCommissionApplied() || responseSimulationOpportunities?.financing_gained_points"
          class="card-subtitle mt-4xs flex justify-between"
          data-testid="gains-subtitle"
        >
          Seus ganhos
          <SimulationTooltip
            v-if="!(props.project.complete && isDetails) && (hasInsuranceApplied() || responseSimulationOpportunities?.financing_gained_points)"
            position="left"
            :text="props.project.complete && isDetails
              ? ''
              : formattedTextTooltip(strengthAddons)"
            data-testid="gains-tooltip"
          >
            <SimulationSignalBar :signal="{ strength: strengthAddons }" />
          </SimulationTooltip>
        </h3>
        <div class="container-details">
          <ul class="flex flex-col">
            <li v-if="responseSimulationOpportunities?.financing_gained_points" class="details" data-testid="solfacil-points">
              <span class="text-brand-primary-medium">{{ $t('solfacilPoints.label') }}</span>
              <span class="text-brand-primary-pure">
                {{ totalPoints.toLocaleString('pt-BR') }}
              </span>
            </li>
            <li v-if="hasCommissionApplied()" class="details" data-testid="commissions">
              <span class="text-brand-primary-medium">Comissões</span>

              <SimulationTooltip
                position="left"
                :text="commissionValues"
                data-testid="commissions-tooltip"
              >
                <span class="text-brand-primary-pure">
                  + {{
                    formatMoney(getSumOfInsurancesAndCommissions())
                  }}
                </span>
              </SimulationTooltip>
            </li>

            <li v-if="!hasCommissionApplied() && hasInsuranceApplied()" class="details" data-testid="insurance-commission">
              <span class="text-brand-primary-medium">Comissão do Seguro</span>
              <span class="text-brand-primary-pure">
                + {{
                  formatMoney(getInsurancePartnerGainSum())
                }}
              </span>
            </li>
          </ul>

          <div v-if="routeName === '/simulation/details/[id]'" class="flex justify-center mt-4xs" data-testid="edit-section">
            <PriceUpdateModal
              :is-open="priceUpdateModal"
              :handle-close="() => priceUpdateModal = false"
              :on-primary-action="() => router.push(`/simulation/revision/${id}`)"
              :on-secondary-action="() => priceUpdateModal = false"
            />

            <SolButton
              v-if="route.query.edit !== undefined && canEditSimulation"
              id="enable-edit"
              data-testid="enable-edit-button"
              class="w-full"
              :disabled="isLoadingProposalChanges || isErrorProposalChanges || isLoadingProjectContractSigned"
              :loading="isLoadingProposalChanges"
              @click="proposalChanges?.value_changed
                ? priceUpdateModal = true
                : router.push(`/simulation/revision/${id}`)"
            >
              Habilitar Edição
              <template #icon:left>
                <EditIcon />
              </template>
            </SolButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.container-details {
  @apply w-full flex flex-col pt-nano;
  .details {
    @apply flex justify-between py-quark text-3xs;

    &:not(:last-of-type) {
      @apply border-b-neutral-high-medium border-b-1
    }
  }
}

.installment-amount {
  font-family: "Lato",sans-serif;
  line-height: 1.15;
  font-size: 1.5rem;
  font-weight: 700;
  @apply text-brand-secondary-pure;
}

.installment-size {
  @apply fonts-body-small-regular;
}

.discount {
  @apply text-brand-primary-medium fonts-subtitle-small;
  @apply text-neutral-low-light
  text-decoration: line-through;
}

.card-subtitle {
  @apply fonts-body-large-bold;
}

.resume-container {
  @apply p-4xs px-xs bg-neutral-high-pure rounded-lg mb-lg mt-2xs;
  @apply sticky top-4 h-max;

  .resume-card {
    @apply bg-neutral-high-light rounded-md p-4xs align-middle text-center;
    @apply flex flex-col;
    .resume-value {
      font-family: "Lato",sans-serif;
      line-height: 1.15;
      font-weight: 700;
      font-size: 1.5rem;
    }
    .card-title {
      @apply fonts-heading-h4 mb-2xs;
    }
    .card-content {
      @apply grid-cols-1 grid-rows-2 grid gap-1;
    }
  }
}
</style>
